import {
  Button,
  Col,
  Divider,
  Form,
  message,
  Row,
  Select,
  Space,
  Spin,
  Upload,
} from "antd";
import React, { useEffect, useState } from "react";
import {
  UsbOutlined,
  PaperClipOutlined,
  UploadOutlined,
  DeleteOutlined,
  CheckCircleOutlined,
  DownloadOutlined,
} from "@ant-design/icons";

import classes from "./style/PageDocumentsSigning.module.css";
import { Logo } from "../../components/Logo/Logo";
import { useDataCrypto } from "../../utils/utilities/crypto-pro/useDataCrypto";
import { Certificate } from "crypto-pro";
import { TagTranslation } from "crypto-pro/lib/helpers/_parseCertInfo.d";

import { RcFile } from "antd/lib/upload";
import { getSignedFile } from "../../utils/utilities/crypto-pro/getSignedFile";
import { Link } from "react-router-dom";

type SignedDocument = {
  uid: string;
  file: File;
};

export const PageDocumentsSigning = () => {
  const { dataCrypto } = useDataCrypto();
  const { Option } = Select;

  const [uploadedDocuments, setUploadedDocuments] = useState<RcFile[]>([]);
  const [signedDocuments, setSignedDocuments] = useState<SignedDocument[]>([]);
  const [isSigning, setIsSigning] = useState(false);

  useEffect(() => {
    window.scrollTo({
      top: document.body.scrollHeight,
      left: 0,
      behavior: "smooth",
    });
  }, [uploadedDocuments]);

  const [thumbprint, setThumbprint] = React.useState(""); // отпечаток ЭЦП

  const handleSelectSign = (value: string) => {
    setThumbprint(value);
  };

  const handleUploadingDocuments = (uploadFiles: RcFile[]) => {
    setSignedDocuments([]);
    setUploadedDocuments([...uploadedDocuments, ...uploadFiles]);
  };

  const handleDeleteDocument = (fileId: string) => {
    const updatedFileList = uploadedDocuments.filter((document) => {
      return document.uid !== fileId;
    });
    setUploadedDocuments(updatedFileList);
  };

  const handleSignDocuments = async () => {
    setIsSigning(true);
    const res: SignedDocument[] = [];
    let isSignSuccess = true;
    for await (const uploadedDocument of uploadedDocuments) {
      try {
        const signedFile = await getSignedFile({
          fileName: uploadedDocument.name,
          file: uploadedDocument,
          thumbprint,
        });
        if (signedFile) {
          res.push({ uid: uploadedDocument.uid, file: signedFile });
        }
      } catch {
        isSignSuccess = false;
        break;
      }
    }
    setIsSigning(false);
    if (isSignSuccess) {
      message.success("Документы успешно подписаны");
      setUploadedDocuments([]);
      setSignedDocuments(res);
    }
  };

  const handleDownloadAllSignedDocuments = () => {
    signedDocuments.forEach((signedDocument) => {
      const data = new Blob([signedDocument.file], {
        type: signedDocument.file.type,
      });
      const csvURL = window.URL.createObjectURL(data);
      const tempLink = document.createElement("a");
      tempLink.href = csvURL;
      tempLink.setAttribute("download", signedDocument.file.name);
      tempLink.click();
    });
  };

  const handleDownloadSignedDocument = (uid: string) => {
    const signedDocumentForDownload = signedDocuments.find((signedDocument) => {
      return signedDocument.uid === uid;
    });
    if (signedDocumentForDownload) {
      const data = new Blob([signedDocumentForDownload.file], {
        type: signedDocumentForDownload.file.type,
      });
      const csvURL = window.URL.createObjectURL(data);
      const tempLink = document.createElement("a");
      tempLink.href = csvURL;
      tempLink.setAttribute("download", signedDocumentForDownload.file.name);
      tempLink.click();
    }
  };

  const handleClearUploadedDocuments = () => {
    setUploadedDocuments([]);
  };
  return (
    <Row
      style={{
        margin: "0",
        height: "100vh",
      }}
    >
      <Col xs={0} sm={9} md={7} lg={8} xl={8}>
        <div className={classes.sideBar} />
      </Col>
      <Col xs={24} sm={15} md={17} lg={16} xl={16}>
        <div className={classes.container}>
          <div className={classes.header}>
            <Logo />
            <Divider type="vertical" />
            <div>Сервис для получения банковских гарантий</div>

            <Space style={{ marginLeft: "auto" }}>
              <Link to={"/"} onClick={() => localStorage.setItem("url", "")}>
                <Button type={"default"}>Перейти на ТатБГ</Button>
              </Link>
            </Space>
          </div>
          <h1 className={classes.title}>Подписание документов</h1>
          <label className={classes.clientLoginFormLabel}>
            <h4 className={classes.signLableTitle}>Выберите ЭЦП</h4>
            <Select
              size="large"
              onChange={handleSelectSign}
              onClick={() => null}
              placeholder={
                <React.Fragment>
                  <UsbOutlined /> {" "}
                  <span className={classes.placeholderSize}> ЭЦП... </span>
                </React.Fragment>
              }
            >
              {dataCrypto?.map((certificate: Certificate) => {
                const cspNames: string[] = [];
                certificate
                  .getOwnerInfo()
                  .then((decodedOwnerInfo: TagTranslation[]) => {
                    cspNames.push(
                      `${
                        decodedOwnerInfo.find(
                          (info) => info.title === "Владелец"
                        )?.description
                      } (ИНН ${
                        decodedOwnerInfo.find((info) => info.title === "ИНН")
                          ?.description
                      })`
                    );
                  });
                return (
                  <Option
                    key={certificate.thumbprint}
                    value={certificate.thumbprint}
                  >
                    <Form key={certificate.thumbprint}>
                      <Form.Item className={classes.singModalInput}>
                        {cspNames}
                      </Form.Item>
                    </Form>
                  </Option>
                );
              })}
            </Select>
          </label>

          <Divider
            orientation="left"
            orientationMargin="0"
            className={classes.signingTitle}
          >
            <h3>Документы для подписания</h3>
          </Divider>
          <div className={classes.fileExtentions}>
            Доступные для загрузки форматы документов: doc, docx, pdf, jpeg,
            png.
          </div>

          {/* Загрузка документов */}
          {!isSigning && (
            <Upload.Dragger
              multiple
              fileList={[]}
              showUploadList={false}
              beforeUpload={(files, fileList) => {
                handleUploadingDocuments(fileList);
                return false;
              }}
            >
              <p className="ant-upload-drag-icon">
                <UploadOutlined />
              </p>
              <p className="ant-upload-text">Загрузить документы</p>
            </Upload.Dragger>
          )}
          {isSigning && (
            <div className={classes.signingAnimation}>
              <Spin spinning={isSigning} size="large"></Spin>
            </div>
          )}
          <div className={classes.uploadedDocuments}>
            {uploadedDocuments.map((document) => {
              return (
                <div key={document.uid} className={classes.uploadedDocumentRow}>
                  <PaperClipOutlined />
                  <span className={classes.uploadedFileName}>
                    {document.name}
                  </span>
                  <DeleteOutlined
                    className={classes.deleteButton}
                    onClick={() => handleDeleteDocument(document.uid)}
                  />
                </div>
              );
            })}
            {!!signedDocuments.length &&
              signedDocuments.map((signedDocument) => {
                return (
                  <div
                    key={signedDocument.uid}
                    className={classes.uploadedDocumentRow}
                  >
                    <CheckCircleOutlined
                      className={classes.signedDocumentIcon}
                    />
                    <span className={classes.uploadedFileName}>
                      {signedDocument.file.name}
                    </span>
                    <DownloadOutlined
                      className={classes.deleteButton}
                      onClick={() =>
                        handleDownloadSignedDocument(signedDocument.uid)
                      }
                    />
                  </div>
                );
              })}
          </div>
          {(!!signedDocuments.length || !!uploadedDocuments.length) && (
            <div className={classes.footer}>
              <div className={classes.signingButtonContainer}>
                {!!signedDocuments.length && (
                  <div className={classes.signingSuccessMessage}>
                    <CheckCircleOutlined
                      className={classes.signingSuccessIcon}
                    />
                    <div>Подписание прошло успешно</div>
                  </div>
                )}

                {!!uploadedDocuments.length && (
                  <Space style={{ marginLeft: "auto" }}>
                    <Button
                      type="default"
                      className={classes.signingButton}
                      onClick={handleClearUploadedDocuments}
                      disabled={isSigning}
                    >
                      Очистить список
                    </Button>
                    <Button
                      type="primary"
                      className={classes.signingButton}
                      onClick={handleSignDocuments}
                      disabled={isSigning}
                    >
                      Подписать документы
                    </Button>
                  </Space>
                )}
                {!!signedDocuments.length && (
                  <Button
                    type="primary"
                    className={classes.downloadDocumentsButton}
                    onClick={handleDownloadAllSignedDocuments}
                  >
                    Скачать подписанные документы
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>
      </Col>
    </Row>
  );
};
